import { MouseEvent, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ButtonBase } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { useAuth } from '@sbv-clients/shared-feature-auth';
import { Box, Icon, LetterAvatar, useTheme } from '@sbv-clients/ui-shared';

import {
  ImpersonatedLabel,
  ImpersonatedLabelMobileView,
} from '../navbar/impersonated-label';
import { useProfileSwitch } from '../navbar/use-profile-switch';
import { SideBarMenuItem } from '../sidebar/types';
import { AccountMenu, AccountMenuOptions } from './account-menu';
import { NavIcon } from './nav-icons';
import { SideMenu } from './side-menu';
import { isLinkActive, NavSection, useNavModel } from './use-nav-model';

// Full menu from this width; below it the bar shows the Menu button.
const TOP_NAV_DESKTOP_MIN = 1100;

interface TopNavProps extends AccountMenuOptions {
  items: SideBarMenuItem[];
  showMenu?: boolean;
  breadCrumbLabels?: { label: string }[];
  profileImage?: string;
}

const DesktopSection = ({
  section,
  pathname,
  alignRight,
}: {
  section: NavSection;
  pathname: string;
  alignRight: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hasDropdown = section.children.length > 1;
  // Clicking the section goes to its first page without opening the menu;
  // the menu comes back on the next hover.
  const [menuOff, setMenuOff] = useState(false);

  return (
    <Box
      onMouseLeave={(e: MouseEvent<HTMLElement>) => {
        // Only when the pointer really left (page loads can fire a leave
        // while it's still over the item).
        const r = e.currentTarget.getBoundingClientRect();
        const inside =
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom;
        if (!inside) setMenuOff(false);
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: 56,
        '&:hover > .nav-label:not(.on), &:focus-within > .nav-label:not(.on)': {
          background: theme.custom?.shellNavHoverBg,
          color: theme.background.white,
        },
        '&:hover .nav-chevron, &:focus-within .nav-chevron': {
          transform: 'rotate(180deg)',
        },
        '&:hover > .nav-dd, &:focus-within > .nav-dd': {
          display: menuOff ? 'none' : 'grid',
        },
        ...(menuOff && {
          '& .nav-chevron': { transform: 'none !important' },
        }),
      }}
    >
      <ButtonBase
        className={`nav-label${section.active ? ' on' : ''}`}
        onClick={(e: MouseEvent<HTMLElement>) => {
          (e.currentTarget as HTMLElement).blur();
          if (hasDropdown) setMenuOff(true);
          navigate(section.href);
        }}
        aria-haspopup={hasDropdown ? 'menu' : undefined}
        aria-current={section.active ? 'page' : undefined}
        sx={{
          height: 34,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '7px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: section.active ? 600 : 500,
          whiteSpace: 'nowrap',
          color: section.active
            ? theme.background.white
            : theme.custom?.shellNavText,
          background: section.active
            ? theme.custom?.shellNavActiveBg
            : 'transparent',
        }}
      >
        {section.item.label}
        {hasDropdown && (
          <Box
            component="i"
            className="nav-chevron"
            sx={{
              fontStyle: 'normal',
              fontSize: '10px',
              color: section.active
                ? theme.custom?.shellNavActiveText
                : theme.custom?.shellNavChevron,
              transition: 'transform 150ms',
            }}
          >
            ▾
          </Box>
        )}
      </ButtonBase>
      {hasDropdown && (
        <Box
          className="nav-dd"
          role="menu"
          sx={{
            display: 'none',
            position: 'absolute',
            top: 'calc(100% - 4px)',
            ...(alignRight ? { right: 0 } : { left: 0 }),
            zIndex: theme.zIndex.appBar + 1,
            minWidth: 260,
            padding: '6px',
            gap: '1px',
            borderRadius: '14px',
            background: theme.background.white,
            boxShadow: theme.custom?.shellMenuShadow,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -10,
              left: 0,
              right: 0,
              height: 10,
            },
          }}
        >
          {section.children.map((child) => {
            const on = isLinkActive(child.link, pathname);
            return (
              <Box
                key={child.id ?? child.link}
                component={Link}
                role="menuitem"
                to={child.link ?? '/'}
                onClick={(e: MouseEvent<HTMLElement>) =>
                  (e.currentTarget as HTMLElement).blur()
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: 40,
                  padding: '0 12px',
                  borderRadius: '9px',
                  fontSize: '14px',
                  fontWeight: on ? 600 : 500,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  color: on
                    ? theme.custom?.shellMenuItemCurrentText
                    : theme.custom?.shellMenuItemText,
                  background: on
                    ? theme.custom?.shellMenuItemCurrentBg
                    : 'transparent',
                  '&:hover': {
                    background: on
                      ? theme.custom?.shellMenuItemCurrentBg
                      : theme.custom?.shellMenuItemHoverBg,
                    color: on
                      ? theme.custom?.shellMenuItemCurrentText
                      : theme.custom?.brandNavy,
                  },
                }}
              >
                {child.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const TopNav = ({
  items,
  showMenu = true,
  breadCrumbLabels = [],
  profileImage,
  ...accountOptions
}: TopNavProps) => {
  const theme = useTheme();
  const desktop = theme.breakpoints.up(TOP_NAV_DESKTOP_MIN);
  const compact = theme.breakpoints.down(TOP_NAV_DESKTOP_MIN);
  const { impersonatorId, username } = useAuth();
  const displayUsername = accountOptions.ssoUserName || username;
  const { mainSections, setupSection, currentSection, currentPage, pathname } =
    useNavModel(items);
  const { currentProfile } = useProfileSwitch();

  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const closeSide = useCallback(() => setSideOpen(false), []);
  const openAccount = useCallback(
    (event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget),
    []
  );

  const crumbSection =
    breadCrumbLabels[0]?.label ?? currentSection?.item.label ?? '';
  const crumbPage =
    breadCrumbLabels[breadCrumbLabels.length - 1]?.label ??
    currentPage?.label ??
    crumbSection;
  const facilityName = currentProfile?.label;

  const logoPill = (
    <Box
      component={Link}
      to="/"
      aria-label="Home"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
        height: 46,
        padding: '6px 20px',
        // The glow reaches past the logo; pull it in so the bar keeps its spacing.
        margin: '0 -10px',
        background: theme.custom?.shellLogoGlow,
        '@media (max-width: 599.95px)': {
          width: 40,
          height: 40,
          padding: '3px',
          margin: 0,
          background: theme.custom?.shellLogoGlowSmall,
        },
      }}
    >
      <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
        <Icon name="LogoSVG" customSizes={{ width: 112, height: 34 }} />
      </Box>
      <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
        <Icon name="LogoIconSVG" customSizes={{ width: 34, height: 34 }} />
      </Box>
    </Box>
  );

  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        zIndex: theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        height: 56,
        flexShrink: 0,
        background: theme.custom?.brandNavy,
        padding: '0 20px',
        [compact]: { padding: '0 14px 0 10px' },
      }}
    >
      {!isEmpty(impersonatorId) && (
        <Box sx={{ [compact]: { display: 'none' } }}>
          <ImpersonatedLabel />
        </Box>
      )}

      {showMenu && (
        <ButtonBase
          onClick={() => setSideOpen(true)}
          aria-label="Open menu"
          aria-expanded={sideOpen}
          sx={{
            display: 'none',
            [compact]: { display: 'flex' },
            alignItems: 'center',
            gap: '8px',
            height: 40,
            minWidth: 40,
            padding: { xs: 0, sm: '0 12px 0 10px' },
            justifyContent: 'center',
            borderRadius: '10px',
            flexShrink: 0,
            color: theme.background.white,
            background: theme.custom?.shellNavActiveBg,
            fontSize: '14px',
          }}
        >
          <NavIcon name="menu" size={20} strokeWidth={1.8} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Menu
          </Box>
        </ButtonBase>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          [compact]: { display: 'none' },
        }}
      >
        {logoPill}
      </Box>
      <Box
        sx={{
          display: 'none',
          [compact]: { display: 'flex', alignItems: 'center', ml: '14px' },
        }}
      >
        {logoPill}
      </Box>

      <Box
        sx={{
          display: 'none',
          [compact]: { display: 'block' },
          minWidth: 0,
          flex: 1,
          ml: '16px',
          pl: '14px',
          boxShadow: `inset 1px 0 0 ${theme.custom?.shellCrumbDivider}`,
        }}
      >
        <Box
          component="small"
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: '11.5px',
            lineHeight: '14px',
            color: theme.custom?.shellCrumbText,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {crumbSection}
        </Box>
        <Box
          component="b"
          sx={{
            display: 'block',
            fontSize: { xs: '15px', sm: '14px' },
            fontWeight: 600,
            lineHeight: { xs: '19px', sm: '17px' },
            color: theme.background.white,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {crumbPage}
        </Box>
        {facilityName && (
          <Box
            component="small"
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              gap: '4px',
              fontSize: '11.5px',
              color: theme.custom?.shellCrumbText,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <NavIcon name="building" size={12} />
            {facilityName}
          </Box>
        )}
      </Box>

      {showMenu && (
        <Box
          component="nav"
          aria-label="Main menu"
          sx={{
            display: 'none',
            [desktop]: { display: 'flex' },
            alignItems: 'center',
            ml: '24px',
            minWidth: 0,
          }}
        >
          {mainSections.map((section, index) => (
            <DesktopSection
              key={section.item.id ?? section.item.label}
              section={section}
              pathname={pathname}
              alignRight={index >= mainSections.length - 2}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          ml: 'auto',
          pl: '12px',
          flexShrink: 0,
        }}
      >
        {!isEmpty(impersonatorId) && (
          <Box
            sx={{
              display: 'none',
              [compact]: { display: 'block' },
              '& .MuiIconButton-root': { color: theme.background.white },
            }}
          >
            <ImpersonatedLabelMobileView />
          </Box>
        )}
        {facilityName && (
          <ButtonBase
            onClick={openAccount}
            title={typeof facilityName === 'string' ? facilityName : undefined}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: '7px',
              padding: '0 4px',
              maxWidth: 220,
              color: theme.custom?.shellNavText,
              '& b': { color: theme.background.white },
            }}
          >
            <NavIcon name="building" size={16} />
            <Box
              component="b"
              sx={{
                fontSize: '13.5px',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {facilityName}
            </Box>
          </ButtonBase>
        )}
        <ButtonBase
          onClick={openAccount}
          aria-label="Your account"
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          sx={{ borderRadius: '50%' }}
        >
          <LetterAvatar
            name={displayUsername}
            src={profileImage}
            sx={{
              width: 32,
              height: 32,
              [compact]: { width: 36, height: 36 },
              fontSize: '12.5px',
              fontWeight: 600,
              background: theme.custom?.shellAvatarBg,
              color: theme.background.white,
              boxShadow: `0 0 0 2px ${theme.colors.gold}`,
            }}
          />
        </ButtonBase>
      </Box>

      <AccountMenu
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        profileImage={profileImage}
        setupHref={showMenu ? setupSection?.href : undefined}
        options={accountOptions}
      />
      {showMenu && (
        <SideMenu open={sideOpen} onClose={closeSide} items={items} />
      )}
    </Box>
  );
};
